/**
 * Compliance Crosswalk Page - Regulatory compliance mapping and analysis
 *
 * @description Maps AIVSS security metrics to regulatory frameworks including
 *              EU AI Act, NIST AI RMF, custom frameworks, and company policies
 * @copyright 2026 Guardrail Technologies
 */

import { useState, useRef, useCallback, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api';
import {
  ArrowLeft,
  Scale,
  Shield,
  FileText,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Plus,
  Building2,
  Printer,
  Trash2,
} from 'lucide-react';
import { Card, Button, Label, Heading, Body, TrafficLight, Badge } from '@/components/tamagui';
import { Text } from 'tamagui';
import { cn } from '@/lib/utils';

// Data
import {
  aivssMetrics as sampleMetrics,
  regulatoryMappings as sampleMappings,
  customFrameworks,
  companyPolicies,
  defaultPrintSettings,
} from '@/data/complianceData';

// Utils
import {
  getEUAIActRiskLevel,
  getNISTFunctionColor,
  calculateComplianceStats,
  getAllComplianceGaps,
  evaluatePolicyRequirement,
  deriveComplianceFromScan,
} from '@/lib/compliance-utils';

import type { ScanDataForCompliance } from '@/types/compliance';

// Components
import { Button as UIButton } from '@/components/ui/Button';
import { ComplianceStatusBadge } from '@/components/compliance/ComplianceStatusBadge';
import { PrintStyles } from '@/components/report/PrintStyles';
import { ReportSubHeader } from '@/components/report/ReportSubHeader';
import { PageBackground, PageSpinner } from '@/components/ui/PageLayout';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

const ComplianceCrosswalkPage = () => {
  useDocumentTitle('Compliance Crosswalk');
  const reportRef = useRef<HTMLDivElement>(null);
  const [activeTab, setActiveTab] = useState('eu-ai-act');
  const [expandedMetrics, setExpandedMetrics] = useState<string[]>([]);

  // Fetch scan data following the BusinessSummaryPage pattern
  const { data: scanData, isLoading } = useQuery<ScanDataForCompliance | null>({
    queryKey: ['compliance-crosswalk-scan'],
    queryFn: async () => {
      const urlParams = new URLSearchParams(window.location.search);

      // Priority 1: Fetch by scan_id from URL
      const scanId = urlParams.get('scan_id') || urlParams.get('scanId') || urlParams.get('id');
      if (scanId) {
        try {
          const result = await apiClient.getScanFull(scanId);
          const raw = (result.scan || result.data || result) as Record<string, unknown>;
          const r = (raw.result || {}) as Record<string, unknown>;
          return {
            scores: r.scores || raw.scores || { aivss: 0, cvss: 0, aars: 0 },
            aarsBreakdown: r.aarsBreakdown || raw.aarsBreakdown,
            findingSummary: r.findingSummary || raw.findingSummary,
            findings: r.findings || raw.findings,
          } as ScanDataForCompliance;
        } catch (e) {
          console.warn('[ComplianceCrosswalk] API fetch error:', e instanceof Error ? e.message : 'Unknown error');
        }
      }

      // Priority 2: Fetch latest scan
      try {
        const result = await apiClient.getScanHistoryFull(1);
        const scans = (result.scans || result.data || []) as unknown[];
        if (scans.length > 0) {
          const raw = scans[0] as Record<string, unknown>;
          const r = (raw.result || {}) as Record<string, unknown>;
          return {
            scores: r.scores || raw.scores || { aivss: 0, cvss: 0, aars: 0 },
            aarsBreakdown: r.aarsBreakdown || raw.aarsBreakdown,
            findingSummary: r.findingSummary || raw.findingSummary,
            findings: r.findings || raw.findings,
          } as ScanDataForCompliance;
        }
      } catch (e) {
        console.warn('[ComplianceCrosswalk] Latest scan fetch error:', e instanceof Error ? e.message : 'Unknown error');
      }

      return null;
    },
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
  });

  // Derive compliance from scan data, or fall back to sample data
  const { metrics, mappings } = scanData
    ? deriveComplianceFromScan(scanData)
    : { metrics: sampleMetrics, mappings: sampleMappings };

  // Use default print settings
  const printSettings = defaultPrintSettings;

  // Framework selection for the compliance report
  const enabledFrameworks = {
    euAIAct: true,
    nistAIRMF: true,
    customFrameworks: true,
    companyPolicy: true,
  };

  // Get decision criticality and calculate EU risk level
  const decisionCriticalityMetric = metrics.find((m) => m.id === 'decision-criticality');
  const euRiskLevel = getEUAIActRiskLevel(decisionCriticalityMetric?.value || 5);

  // Calculate compliance statistics
  const { euStats, nistStats } = calculateComplianceStats(mappings);

  // Get all compliance gaps
  const complianceGaps = getAllComplianceGaps(mappings);

  // Toggle metric expansion
  const toggleMetric = (metricId: string) => {
    setExpandedMetrics((prev) =>
      prev.includes(metricId) ? prev.filter((id) => id !== metricId) : [...prev, metricId]
    );
  };

  // Tab definitions for ARIA tablist
  const tabs = [
    { key: 'eu-ai-act', label: 'EU AI Act', enabled: enabledFrameworks.euAIAct },
    { key: 'nist-ai-rmf', label: 'NIST AI RMF', enabled: enabledFrameworks.nistAIRMF },
    { key: 'custom-frameworks', label: 'Custom Frameworks', enabled: enabledFrameworks.customFrameworks },
    { key: 'company-policy', label: 'Company Policy', enabled: enabledFrameworks.companyPolicy },
    { key: 'regulatory-summary', label: 'Regulatory Summary', enabled: true },
  ].filter((t) => t.enabled);

  // Keyboard navigation for tablist (Arrow Left/Right, Home, End)
  const handleTabKeyDown = useCallback(
    (e: ReactKeyboardEvent<HTMLElement>) => {
      const currentIndex = tabs.findIndex((t) => t.key === activeTab);

      let nextIndex: number;
      switch (e.key) {
        case 'ArrowRight':
          nextIndex = (currentIndex + 1) % tabs.length;
          break;
        case 'ArrowLeft':
          nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
          break;
        case 'Home':
          nextIndex = 0;
          break;
        case 'End':
          nextIndex = tabs.length - 1;
          break;
        default:
          return;
      }

      e.preventDefault();
      setActiveTab(tabs[nextIndex].key);

      // Move focus to the newly-active tab button
      const tabList = (e.target as HTMLElement).closest('[role="tablist"]');
      const buttons = tabList?.querySelectorAll<HTMLButtonElement>('[role="tab"]');
      buttons?.[nextIndex]?.focus();
    },
    [activeTab, tabs],
  );

  if (isLoading) {
    return <PageSpinner label="Loading compliance data…" />;
  }

  return (
    <PageBackground>
      {/* Traffic Light Accent Bar */}
      <div className="h-1 w-full bg-gradient-to-r from-red-600 via-yellow-500 to-green-500" />

      <PrintStyles />

      <ReportSubHeader
        title="Compliance Crosswalk"
        subtitle="EU AI Act · NIST AI RMF · Custom Frameworks"
        actions={
          <>
            <UIButton onClick={() => window.print()} className="gap-2">
              <Printer className="w-4 h-4" />
              Print / Save as PDF
            </UIButton>
            <UIButton
              variant="outline"
              className="gap-2"
              onClick={() => {
                window.close();
                setTimeout(() => {
                  window.location.href = '/reports';
                }, 300);
              }}
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Reports
            </UIButton>
          </>
        }
      />

      <div ref={reportRef} className="print-container container mx-auto p-4 md:p-8">
        {/* Plugin and Generation Info */}
        {printSettings.showHeader && (
          <div className="print-section mb-4 text-center">
            <Body className="text-sm text-zinc-400">
              Plugin: <span className="font-medium text-white">feature-dev</span> | Generated:{' '}
              {new Date().toLocaleDateString()}
            </Body>
          </div>
        )}

        {/* Page Title Section */}
        {printSettings.showHeader && (
          <div className="print-section mb-8 text-center">
            <div className="mb-6">
              <Heading level={1} className="mb-2 text-white">
                Compliance Crosswalk Report
              </Heading>
            </div>
          </div>
        )}

        {/* Summary Cards */}
        {printSettings.showSummary && (
          <div className="print-section grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <Card className="bg-zinc-900/50 border-zinc-800">
              <div className="p-4">
                <Label className="text-sm font-medium text-zinc-400 mb-2">
                  EU AI Act Risk Level
                </Label>
                <div className="flex items-center gap-2">
                  <Badge variant="risk" level={euRiskLevel.level}>
                    {euRiskLevel.euCategory}
                  </Badge>
                </div>
                <Body className="text-xs text-zinc-500 mt-2">{euRiskLevel.description}</Body>
              </div>
            </Card>

            <Card className="bg-zinc-900/50 border-zinc-800">
              <div className="p-4">
                <Label className="text-sm font-medium text-zinc-400 mb-2">
                  EU AI Act Compliance
                </Label>
                <div className="flex items-center gap-2">
                  <span className="text-2xl font-bold text-white">
                    {Math.round((euStats.compliant / mappings.length) * 100)}%
                  </span>
                </div>
                <div className="w-full bg-zinc-800 rounded-full h-2 mt-2">
                  <div
                    className="bg-green-600 h-2 rounded-full"
                    style={{ width: `${(euStats.compliant / mappings.length) * 100}%` }}
                  />
                </div>
                <Body className="text-xs text-zinc-500 mt-2">
                  {euStats.compliant} compliant, {euStats.partial} partial, {euStats.nonCompliant}{' '}
                  gaps
                </Body>
              </div>
            </Card>

            <Card className="bg-zinc-900/50 border-zinc-800">
              <div className="p-4">
                <Label className="text-sm font-medium text-zinc-400 mb-2">
                  NIST AI RMF Compliance
                </Label>
                <div className="flex items-center gap-2">
                  <span className="text-2xl font-bold text-white">
                    {Math.round((nistStats.compliant / mappings.length) * 100)}%
                  </span>
                </div>
                <div className="w-full bg-zinc-800 rounded-full h-2 mt-2">
                  <div
                    className="bg-green-600 h-2 rounded-full"
                    style={{ width: `${(nistStats.compliant / mappings.length) * 100}%` }}
                  />
                </div>
                <Body className="text-xs text-zinc-500 mt-2">
                  {nistStats.compliant} compliant, {nistStats.partial} partial,{' '}
                  {nistStats.nonCompliant} gaps
                </Body>
              </div>
            </Card>

            <Card className="bg-zinc-900/50 border-zinc-800">
              <div className="p-4">
                <Label className="text-sm font-medium text-zinc-400 mb-2">
                  Total Compliance Gaps
                </Label>
                <div className="flex items-center gap-2">
                  <span className="text-2xl font-bold text-red-500">{complianceGaps.length}</span>
                  <AlertTriangle className="w-5 h-5 text-red-500" />
                </div>
                <Body className="text-xs text-zinc-500 mt-2">
                  Requires remediation for full compliance
                </Body>
              </div>
            </Card>
          </div>
        )}

        {/* Risk Summary Cards */}
        {printSettings.showSummary && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8 print-section">
            <Card className="p-6 bg-zinc-900/50 border-zinc-800">
              <div className="flex items-center justify-between mb-4">
                <Heading level={3} className="text-white">
                  EU AI Act Status
                </Heading>
                <TrafficLight
                  active={
                    euRiskLevel.level === 'critical' || euRiskLevel.level === 'high'
                      ? 'red'
                      : euRiskLevel.level === 'medium'
                        ? 'yellow'
                        : 'green'
                  }
                />
              </div>
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Badge variant="risk" level={euRiskLevel.level} />
                  <Body className="text-zinc-300">{euRiskLevel.euCategory} Risk System</Body>
                </div>
                <div className="text-2xl font-bold text-white">
                  {Math.round((euStats.compliant / mappings.length) * 100)}%
                </div>
                <Body className="text-zinc-400">Compliance Rate</Body>
              </div>
            </Card>

            <Card className="p-6 bg-zinc-900/50 border-zinc-800">
              <div className="flex items-center justify-between mb-4">
                <Heading level={3} className="text-white">
                  Total Compliance Gaps
                </Heading>
                <AlertTriangle className="w-6 h-6 text-risk-high" />
              </div>
              <div className="space-y-3">
                <div className="text-2xl font-bold text-risk-high">{complianceGaps.length}</div>
                <Body className="text-zinc-400">Requires remediation for full compliance</Body>
              </div>
            </Card>

            <Card className="p-6 bg-zinc-900/50 border-zinc-800">
              <div className="flex items-center justify-between mb-4">
                <Heading level={3} className="text-white">
                  NIST AI RMF Status
                </Heading>
                <Shield className="w-6 h-6 text-green-500" />
              </div>
              <div className="space-y-3">
                <div className="text-2xl font-bold text-white">
                  {Math.round((nistStats.compliant / mappings.length) * 100)}%
                </div>
                <Body className="text-zinc-400">Compliance Rate</Body>
              </div>
            </Card>
          </div>
        )}

        {/* Main Content */}
        <div className="print-section">
          {/* Framework Tabs — WAI-ARIA tablist with keyboard navigation */}
          <div role="tablist" aria-label="Compliance frameworks" className="flex flex-wrap gap-2 mb-6 no-print">
            {tabs.map(({ key, label }) => {
              const isActive = activeTab === key;
              return (
                <Button
                  key={key}
                  role="tab"
                  id={`crosswalk-tab-${key}`}
                  aria-selected={isActive}
                  aria-controls={`crosswalk-panel-${key}`}
                  tabIndex={isActive ? 0 : -1}
                  variant={isActive ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setActiveTab(key)}
                  onKeyDown={handleTabKeyDown}
                  className={
                    isActive
                      ? 'bg-green-600 hover:bg-green-700 text-white'
                      : 'border-zinc-600 text-white hover:bg-zinc-800'
                  }
                >
                  <Text>{label}</Text>
                </Button>
              );
            })}
          </div>

          {/* EU AI Act Tab */}
          {activeTab === 'eu-ai-act' && enabledFrameworks.euAIAct && (
            <div role="tabpanel" id="crosswalk-panel-eu-ai-act" aria-labelledby="crosswalk-tab-eu-ai-act" className={cn(printSettings.showEUAIAct ? 'print-section' : 'print-only')}>
              <div className={cn('print-only mb-4', !printSettings.showEUAIAct && 'hidden')}>
                <Heading level={2} className="text-white">
                  EU AI Act Compliance
                </Heading>
              </div>
              <Card className="bg-zinc-900/50 border-zinc-800">
                <div className="p-6">
                  <Heading level={3} className="flex items-center gap-2 mb-4 text-white">
                    <Scale className="w-5 h-5 text-green-500" />
                    EU AI Act Mapping
                  </Heading>
                  <Body className="mb-6 text-zinc-300">
                    Mapping of AIVSS metrics to EU AI Act requirements
                  </Body>

                  {/* Risk Classification */}
                  <div className="p-4 border border-zinc-700 rounded-lg bg-zinc-800/50 mb-6">
                    <Label className="font-medium mb-2 text-white">
                      Risk Classification (Based on Decision Criticality:{' '}
                      {decisionCriticalityMetric?.value.toFixed(1)}/10)
                    </Label>
                    <div className="flex items-center gap-4">
                      <Badge variant="risk" level={euRiskLevel.level}>
                        {euRiskLevel.euCategory} Risk
                      </Badge>
                      <Body className="text-sm text-zinc-300">{euRiskLevel.description}</Body>
                    </div>
                  </div>

                  {/* Metric Mappings */}
                  <div className="space-y-2">
                    {mappings.map((mapping) => {
                      const metric = metrics.find((m) => m.id === mapping.metricId);
                      if (!metric) return null;

                      return (
                        <div
                          key={mapping.metricId}
                          className="border border-zinc-700 rounded-lg bg-zinc-800/30"
                        >
                          <div
                            className="flex items-center justify-between p-4 cursor-pointer hover:bg-zinc-800/50 transition-colors"
                            onClick={() => toggleMetric(mapping.metricId)}
                          >
                            <div className="flex items-center gap-4">
                              {expandedMetrics.includes(mapping.metricId) ? (
                                <ChevronDown className="w-4 h-4 text-zinc-400" />
                              ) : (
                                <ChevronRight className="w-4 h-4 text-zinc-400" />
                              )}
                              <div>
                                <Label className="font-medium text-white">{metric.name}</Label>
                                <Body className="text-sm text-zinc-400">{metric.description}</Body>
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              <span className="text-sm font-medium text-white">
                                {metric.value.toFixed(1)}/10
                              </span>
                              <ComplianceStatusBadge status={mapping.euAIAct.status} />
                            </div>
                          </div>
                          {expandedMetrics.includes(mapping.metricId) && (
                            <div className="p-4 border-t border-zinc-700 bg-zinc-800/50 space-y-3">
                              <div>
                                <Label className="text-sm font-medium mb-1 text-white">
                                  Applicable Articles
                                </Label>
                                <div className="flex flex-wrap gap-2">
                                  {mapping.euAIAct.articles.map((article, i) => (
                                    <Badge
                                      key={i}
                                      className="text-xs border border-zinc-700 bg-zinc-800 text-zinc-300"
                                    >
                                      {article}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                              <div>
                                <Label className="text-sm font-medium mb-1 text-white">
                                  Requirements
                                </Label>
                                <ul className="text-sm text-zinc-400 list-disc list-inside">
                                  {mapping.euAIAct.requirements.map((req, i) => (
                                    <li key={i}>{req}</li>
                                  ))}
                                </ul>
                              </div>
                              {mapping.euAIAct.gaps.length > 0 && (
                                <div>
                                  <Label className="text-sm font-medium mb-1 text-risk-high">
                                    Compliance Gaps
                                  </Label>
                                  <ul className="text-sm text-risk-high list-disc list-inside">
                                    {mapping.euAIAct.gaps.map((gap, i) => (
                                      <li key={i}>{gap}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </Card>
            </div>
          )}

          {/* NIST AI RMF Tab */}
          {activeTab === 'nist-ai-rmf' && enabledFrameworks.nistAIRMF && (
            <div role="tabpanel" id="crosswalk-panel-nist-ai-rmf" aria-labelledby="crosswalk-tab-nist-ai-rmf" className={cn(printSettings.showNISTAIRMF ? 'print-section' : 'print-only')}>
              <div className={cn('print-only mb-4', !printSettings.showNISTAIRMF && 'hidden')}>
                <Heading level={2} className="text-white">
                  NIST AI RMF Compliance
                </Heading>
              </div>
              <Card className="bg-zinc-900/50 border-zinc-800">
                <div className="p-6">
                  <Heading level={3} className="flex items-center gap-2 mb-4 text-white">
                    <Shield className="w-5 h-5 text-green-500" />
                    NIST AI Risk Management Framework Mapping
                  </Heading>
                  <Body className="mb-6 text-zinc-300">
                    Mapping to GOVERN, MAP, MEASURE, and MANAGE functions
                  </Body>
                  {/* Function Overview */}
                  <div className="grid grid-cols-4 gap-4 mb-6">
                    {(['GOVERN', 'MAP', 'MEASURE', 'MANAGE'] as const).map((func) => {
                      const count = mappings.filter((m) =>
                        m.nistAIRMF.functions.includes(func)
                      ).length;
                      return (
                        <div
                          key={func}
                          className="text-center p-4 border border-zinc-700 rounded-lg bg-zinc-800/30"
                        >
                          <Badge
                            className={cn('mb-2', getNISTFunctionColor(func), 'text-zinc-900')}
                          >
                            {func}
                          </Badge>
                          <div className="text-2xl font-bold text-white">{count}</div>
                          <Body className="text-xs text-zinc-400">Mapped Metrics</Body>
                        </div>
                      );
                    })}
                  </div>

                  {/* Metric Mappings */}
                  <div className="space-y-2">
                    {mappings.map((mapping) => {
                      const metric = metrics.find((m) => m.id === mapping.metricId);
                      if (!metric) return null;

                      return (
                        <div
                          key={mapping.metricId}
                          className="border border-zinc-700 rounded-lg bg-zinc-800/30"
                        >
                          <div
                            className="flex items-center justify-between p-4 cursor-pointer hover:bg-zinc-800/50 transition-colors"
                            onClick={() => toggleMetric(`nist-${mapping.metricId}`)}
                          >
                            <div className="flex items-center gap-4">
                              {expandedMetrics.includes(`nist-${mapping.metricId}`) ? (
                                <ChevronDown className="w-4 h-4 text-zinc-400" />
                              ) : (
                                <ChevronRight className="w-4 h-4 text-zinc-400" />
                              )}
                              <div>
                                <Label className="font-medium text-white">{metric.name}</Label>
                                <div className="flex gap-1 mt-1">
                                  {mapping.nistAIRMF.functions.map((func) => (
                                    <Badge
                                      key={func}
                                      className={cn(
                                        'text-xs',
                                        getNISTFunctionColor(func),
                                        'text-zinc-900'
                                      )}
                                    >
                                      {func}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              <span className="text-sm font-medium text-white">
                                {metric.value.toFixed(1)}/10
                              </span>
                              <ComplianceStatusBadge status={mapping.nistAIRMF.status} />
                            </div>
                          </div>
                          {expandedMetrics.includes(`nist-${mapping.metricId}`) && (
                            <div className="p-4 border-t border-zinc-700 bg-zinc-800/50 space-y-3">
                              <div>
                                <Label className="text-sm font-medium mb-1 text-white">
                                  Categories
                                </Label>
                                <div className="flex flex-wrap gap-2">
                                  {mapping.nistAIRMF.categories.map((cat, i) => (
                                    <Badge
                                      key={i}
                                      className="text-xs border border-zinc-700 bg-zinc-800 text-zinc-300"
                                    >
                                      {cat}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                              {mapping.nistAIRMF.gaps.length > 0 && (
                                <div>
                                  <Label className="text-sm font-medium mb-1 text-risk-high">
                                    Compliance Gaps
                                  </Label>
                                  <ul className="text-sm text-risk-high list-disc list-inside">
                                    {mapping.nistAIRMF.gaps.map((gap, i) => (
                                      <li key={i}>{gap}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </Card>
            </div>
          )}

          {/* Custom Frameworks Tab */}
          {activeTab === 'custom-frameworks' && enabledFrameworks.customFrameworks && (
            <div
              role="tabpanel" id="crosswalk-panel-custom-frameworks" aria-labelledby="crosswalk-tab-custom-frameworks"
              className={cn(printSettings.showCustomFrameworks ? 'print-section' : 'print-only')}
            >
              <div
                className={cn('print-only mb-4', !printSettings.showCustomFrameworks && 'hidden')}
              >
                <Heading level={2} className="text-white">
                  Custom Frameworks
                </Heading>
              </div>
              <Card className="bg-zinc-900/50 border-zinc-800">
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <Heading level={3} className="flex items-center gap-2 text-white">
                        <FileText className="w-5 h-5 text-green-500" />
                        Custom Regulatory Frameworks
                      </Heading>
                      <Body className="mt-1 text-zinc-300">
                        Add and manage additional compliance frameworks
                      </Body>
                    </div>
                    <Button size="sm" className="no-print bg-green-600 hover:bg-green-700">
                      <Plus className="w-4 h-4 mr-2" />
                      <Text>Add Framework</Text>
                    </Button>
                  </div>

                  <div className="space-y-4">
                    {customFrameworks.map((framework) => (
                      <div
                        key={framework.id}
                        className="p-4 border border-zinc-700 rounded-lg bg-zinc-800/30"
                      >
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <Label className="font-medium text-white">{framework.name}</Label>
                            <Body className="text-sm text-zinc-300">{framework.description}</Body>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="no-print text-zinc-400 hover:text-red-500"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                        <div className="space-y-2">
                          {framework.requirements.map((req) => (
                            <div
                              key={req.id}
                              className="flex items-center justify-between p-3 bg-zinc-800/50 rounded"
                            >
                              <div>
                                <Body className="text-sm font-medium text-white">{req.name}</Body>
                                <Body className="text-xs text-zinc-400">{req.description}</Body>
                              </div>
                              <ComplianceStatusBadge status={req.status} />
                            </div>
                          ))}
                          {framework.requirements.length === 0 && (
                            <Body className="text-sm text-center py-4 text-zinc-400">
                              No requirements defined yet
                            </Body>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </Card>
            </div>
          )}

          {/* Company Policy Tab */}
          {activeTab === 'company-policy' && enabledFrameworks.companyPolicy && (
            <div role="tabpanel" id="crosswalk-panel-company-policy" aria-labelledby="crosswalk-tab-company-policy" className={cn(printSettings.showCompanyPolicy ? 'print-section' : 'print-only')}>
              <div className={cn('print-only mb-4', !printSettings.showCompanyPolicy && 'hidden')}>
                <Heading level={2} className="text-white">
                  Company Policy Compliance
                </Heading>
              </div>
              <Card className="bg-zinc-900/50 border-zinc-800">
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <Heading level={3} className="flex items-center gap-2 text-white">
                        <Building2 className="w-5 h-5 text-green-500" />
                        Company Policy Comparison
                      </Heading>
                      <Body className="mt-1 text-zinc-300">
                        Compare against internal governance policies
                      </Body>
                    </div>
                    <Button size="sm" className="no-print bg-green-600 hover:bg-green-700">
                      <Plus className="w-4 h-4 mr-2" />
                      <Text>Add Policy</Text>
                    </Button>
                  </div>

                  <div className="space-y-4">
                    {companyPolicies.map((policy) => (
                      <div
                        key={policy.id}
                        className="p-4 border border-zinc-700 rounded-lg bg-zinc-800/30"
                      >
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <Label className="font-medium text-white">{policy.name}</Label>
                            <Body className="text-sm text-zinc-300">{policy.description}</Body>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="no-print text-zinc-400 hover:text-red-500"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                        <div className="space-y-2">
                          {policy.requirements.map((req) => {
                            const metric = metrics.find((m) => m.id === req.mappedMetric);
                            const status = evaluatePolicyRequirement(req, metrics);
                            const operatorSymbol =
                              req.operator === 'gte' ? '≥' : req.operator === 'lte' ? '≤' : '=';

                            return (
                              <div
                                key={req.id}
                                className="flex items-center justify-between p-3 bg-zinc-800/50 rounded"
                              >
                                <div>
                                  <Body className="text-sm font-medium text-white">{req.name}</Body>
                                  <Body className="text-xs text-zinc-400">
                                    {metric?.name}: {metric?.value.toFixed(1)} {operatorSymbol}{' '}
                                    {req.threshold} threshold
                                  </Body>
                                </div>
                                <ComplianceStatusBadge status={status} />
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </Card>
            </div>
          )}

          {/* Regulatory Summary Tab - Temporarily commented out */}
          {activeTab === 'regulatory-summary' && (
            <div role="tabpanel" id="crosswalk-panel-regulatory-summary" aria-labelledby="crosswalk-tab-regulatory-summary" className="bg-zinc-900/50 border-zinc-800 rounded-lg p-6">
              <Heading level={3} className="text-white">
                Regulatory Summary
              </Heading>
              <Body className="text-zinc-300 mt-2">Coming soon...</Body>
            </div>
          )}
        </div>

        {/* Print-only sections */}
        <div className="print-only print-break">
          {printSettings.showEUAIAct && activeTab !== 'eu-ai-act' && (
            <div className="print-section mb-8">
              <h2 className="text-xl font-bold mb-4 text-white">EU AI Act Compliance</h2>
              <div className="space-y-4">
                <div className="p-4 border border-zinc-700 rounded-lg bg-zinc-800/30">
                  <h4 className="font-medium mb-2 text-white">Risk Classification</h4>
                  <p className="text-zinc-300">
                    Level: {euRiskLevel.euCategory} | Decision Criticality:{' '}
                    {decisionCriticalityMetric?.value.toFixed(1)}/10
                  </p>
                  <p className="text-sm text-zinc-400">{euRiskLevel.description}</p>
                </div>
                {mappings.map((mapping) => {
                  const metric = metrics.find((m) => m.id === mapping.metricId);
                  return (
                    <div
                      key={mapping.metricId}
                      className="p-4 border border-zinc-700 rounded-lg bg-zinc-800/30"
                    >
                      <div className="flex justify-between mb-2">
                        <h4 className="font-medium text-white">{metric?.name}</h4>
                        <span className="text-white">
                          {metric?.value.toFixed(1)}/10 - {mapping.euAIAct.status}
                        </span>
                      </div>
                      <p className="text-sm text-zinc-400 mb-2">
                        Articles: {mapping.euAIAct.articles.join(', ')}
                      </p>
                      {mapping.euAIAct.gaps.length > 0 && (
                        <p className="text-sm text-risk-high">
                          Gaps: {mapping.euAIAct.gaps.join('; ')}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {printSettings.showNISTAIRMF && activeTab !== 'nist-ai-rmf' && (
            <div className="print-section mb-8">
              <h2 className="text-xl font-bold mb-4 text-white">NIST AI RMF Compliance</h2>
              <div className="space-y-4">
                {mappings.map((mapping) => {
                  const metric = metrics.find((m) => m.id === mapping.metricId);
                  return (
                    <div
                      key={mapping.metricId}
                      className="p-4 border border-zinc-700 rounded-lg bg-zinc-800/30"
                    >
                      <div className="flex justify-between mb-2">
                        <h4 className="font-medium text-white">{metric?.name}</h4>
                        <span className="text-white">
                          {metric?.value.toFixed(1)}/10 - {mapping.nistAIRMF.status}
                        </span>
                      </div>
                      <p className="text-sm text-zinc-400 mb-2">
                        Functions: {mapping.nistAIRMF.functions.join(', ')} | Categories:{' '}
                        {mapping.nistAIRMF.categories.join(', ')}
                      </p>
                      {mapping.nistAIRMF.gaps.length > 0 && (
                        <p className="text-sm text-risk-high">
                          Gaps: {mapping.nistAIRMF.gaps.join('; ')}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {printSettings.showComplianceGaps && (
            <div className="print-section mb-8">
              <h2 className="text-xl font-bold mb-4 text-white">All Compliance Gaps</h2>
              <div className="space-y-2">
                {complianceGaps.map((gap, i) => (
                  <div key={i} className="p-3 border border-zinc-700 rounded-lg bg-zinc-800/30">
                    <p className="font-medium text-white">
                      {gap.framework} - {gap.article}
                    </p>
                    <p className="text-sm text-zinc-300">{gap.gap}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="mt-8 pt-4 border-t border-zinc-800 text-center text-sm text-zinc-400">
          <p>Generated by Guardrail AI Security Assessment Platform</p>
          <p className="text-xs mt-1">
            This report is for informational purposes and does not constitute legal advice.
          </p>
        </div>
      </div>
    </PageBackground>
  );
};

export default ComplianceCrosswalkPage;
